/**
 * SPEC-001 — OCPP 1.6J §6.5 Local Authorization List management.
 *
 * A versioned per-station cache of authorisation decisions. The CP
 * consults this list BEFORE sending Authorize to the CSMS when
 * `LocalAuthListEnabled=true` AND `LocalAuthorizeOffline=true`. The
 * CSMS keeps the canonical copy and pushes updates via
 * `SendLocalList(updateType=Full|Differential, listVersion, ...)`.
 *
 * Domain layer (this file): pure types + pure operations on a snapshot.
 * No Dexie / no React. Persistence is a separate concern handled by
 * `LocalAuthListRepository` (defined in `@/persistence/ports`).
 */
import { AuthStatus } from './types';

/**
 * Max idTag/idToken length per OCPP version. OCPP 1.6J IdToken is
 * ≤ 20 chars (§7.10); OCPP 2.0.1 IdTokenType.idToken is ≤ 36 chars
 * (Part 2 §IdTokenType). The validator takes the limit as a parameter
 * so the same domain code serves both — the caller (handler) passes
 * the value matching the connected adapter's version.
 */
export const MAX_ID_TAG_LENGTH_V16 = 20;
export const MAX_ID_TAG_LENGTH_V201 = 36;

/** A single entry in the local auth list, matching OCPP 1.6J
 *  AuthorizationData with idTag+idTagInfo flattened for the domain. */
export interface LocalAuthListEntrySnapshot {
  readonly idTag: string;
  readonly status: AuthStatus;
  readonly expiryDate?: string;
  readonly parentIdTag?: string;
}

/**
 * Snapshot of the whole list at a point in time. `listVersion` is the
 * OCPP-spec integer; 0 means "no list seeded yet" (default).
 */
export interface LocalAuthListSnapshot {
  readonly listVersion: number;
  readonly entries: readonly LocalAuthListEntrySnapshot[];
}

/** Differential-update operations — translated 1:1 from
 *  AuthorizationData entries on the wire. */
export type LocalAuthListDiffOp =
  | { readonly kind: 'upsert'; readonly entry: LocalAuthListEntrySnapshot }
  | { readonly kind: 'delete'; readonly idTag: string };

export const LocalAuthListUpdateType = {
  Full: 'Full',
  Differential: 'Differential',
} as const;
export type LocalAuthListUpdateType =
  (typeof LocalAuthListUpdateType)[keyof typeof LocalAuthListUpdateType];

/**
 * Lookup result for the local-first Authorize path. `kind: 'miss'`
 * means the caller should fall through to the CSMS Authorize CALL.
 */
export type LocalAuthLookup =
  | { readonly kind: 'hit'; readonly entry: LocalAuthListEntrySnapshot; readonly expired: boolean }
  | { readonly kind: 'miss' };

/**
 * Validate the wire shape of a single AuthorizationData item before
 * persisting it. Returns the normalised entry or an error string.
 */
export function validateEntry(
  raw: Record<string, unknown>,
  maxIdLength: number = MAX_ID_TAG_LENGTH_V16,
): { readonly ok: true; readonly entry: LocalAuthListEntrySnapshot } | { readonly ok: false; readonly error: string } {
  const idTag = raw['idTag'];
  if (typeof idTag !== 'string' || idTag.length === 0 || idTag.length > maxIdLength) {
    return { ok: false, error: `idTag must be a non-empty string ≤ ${maxIdLength} chars` };
  }
  const info = raw['idTagInfo'];
  if (!info || typeof info !== 'object') {
    return { ok: false, error: 'idTagInfo missing' };
  }
  const i = info as Record<string, unknown>;
  const status = i['status'];
  if (!isAuthStatus(status)) {
    return { ok: false, error: `idTagInfo.status not a valid AuthStatus: ${String(status)}` };
  }
  const out: LocalAuthListEntrySnapshot = {
    idTag,
    status,
    ...(typeof i['expiryDate'] === 'string' ? { expiryDate: i['expiryDate'] } : {}),
    ...(typeof i['parentIdTag'] === 'string' ? { parentIdTag: i['parentIdTag'] } : {}),
  };
  return { ok: true, entry: out };
}

function isAuthStatus(v: unknown): v is AuthStatus {
  return typeof v === 'string' && Object.values(AuthStatus).includes(v as AuthStatus);
}

/**
 * Apply a list of AuthorizationData items as a Full or Differential
 * update. Returns the new entry set (NOT the listVersion — that's the
 * caller's job, since 1.6J says the CSMS supplies the new version).
 *
 * Full   — wholesale replace (any prior entries discarded).
 * Diff   — for each input item: missing idTagInfo on the wire means
 *          "delete this idTag"; present idTagInfo means "upsert".
 */
export function applyUpdate(
  current: readonly LocalAuthListEntrySnapshot[],
  updateType: LocalAuthListUpdateType,
  rawItems: readonly Record<string, unknown>[],
  maxIdLength: number = MAX_ID_TAG_LENGTH_V16,
): { readonly ok: true; readonly next: readonly LocalAuthListEntrySnapshot[] } | { readonly ok: false; readonly error: string } {
  if (updateType === LocalAuthListUpdateType.Full) {
    const next: LocalAuthListEntrySnapshot[] = [];
    for (const raw of rawItems) {
      const r = validateEntry(raw, maxIdLength);
      if (!r.ok) return { ok: false, error: r.error };
      // Last write wins for a duplicate idTag in a single Full update.
      const i = next.findIndex((e) => e.idTag === r.entry.idTag);
      if (i >= 0) next[i] = r.entry;
      else next.push(r.entry);
    }
    return { ok: true, next };
  }
  // Differential.
  const byIdTag = new Map<string, LocalAuthListEntrySnapshot>(current.map((e) => [e.idTag, e]));
  for (const raw of rawItems) {
    const idTag = raw['idTag'];
    if (typeof idTag !== 'string') {
      return { ok: false, error: 'differential entry missing idTag' };
    }
    if (raw['idTagInfo'] === undefined || raw['idTagInfo'] === null) {
      // OCPP 1.6J §6.5: an entry without idTagInfo in a differential
      // update means DELETE that idTag.
      byIdTag.delete(idTag);
      continue;
    }
    const r = validateEntry(raw, maxIdLength);
    if (!r.ok) return { ok: false, error: r.error };
    byIdTag.set(idTag, r.entry);
  }
  return { ok: true, next: Array.from(byIdTag.values()) };
}

/**
 * Lookup an idTag against the snapshot. An entry whose `expiryDate`
 * is in the past is still returned (with `expired: true`) so the
 * caller can decide whether to surface it as Expired vs ignore the
 * cache and ask the CSMS.
 */
export function lookup(
  snapshot: LocalAuthListSnapshot,
  idTag: string,
  nowIso: string = new Date().toISOString(),
): LocalAuthLookup {
  const entry = snapshot.entries.find((e) => e.idTag === idTag);
  if (!entry) return { kind: 'miss' };
  const expired =
    entry.expiryDate !== undefined && entry.expiryDate.length > 0 && entry.expiryDate < nowIso;
  return { kind: 'hit', entry, expired };
}
